// build-names-map.ts — client-side construction of the referenced-names map that
// renderObjectCard / renderItemListDisplay expect ({ UUID => {item_name, item_type, pic_url?} }).
//
// On the SERVER path this map is built by ft_build_referenced_names_map() (ld_ts_mount.php) and
// shipped alongside the blob. On the CACHE path there is no server map, so we port the HARVEST
// half of that builder: walk the found blob and read every node's own items.{item_id,item_name,
// item_type}. This captures names from BOTH the full copies AND the bare shells the dedup left
// behind — shells carry exactly {item_id,item_name,item_type} for precisely this purpose.
//
// First paint uses held data. The list-display reference hydrator fills unnamed references
// afterward through the object read; a truly unavailable id keeps its labelled marker.
// icon is never set here — iconMap() resolves icons from FT_ICON_REGISTRY by id/type.

import { itemsCol, picturesOf } from '../shared/array-types';
import { pageIslandRoots, type CachedItemNode } from './find-cached-item';
import type { ReferencedNames } from './types';

// Do NOT skip `computed` — the finalized caster blob nests full abilities/spells under
// computed.creature.*, and their shells (carrying item_name/item_type) live there too. We still skip
// `items` (scalar identity bucket) and `pictures` (picture rows) — no nestable items there.
const SKIP_KEYS = new Set<string>(['items', 'pictures']);

type NamesMapWindow = Window & {
    FateTide?: {
        casterCache?: unknown;
        objectCache?: unknown;
        encounterCache?: unknown;
    };
};

function recordOf(value: unknown): CachedItemNode | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CachedItemNode
        : null;
}

// Runtime instances borrow their template's items.item_id but may replace items.item_name with an
// instance name. Never publish that mutable name as the canonical UUID identity. Their dedicated
// instance IDs are handled by actor/inventory/status renderers; referenced-item names come from the
// root catalog folded below. Continue recursing through the node so its nested static definitions are
// still harvested.
function isRuntimeItemInstance(node: CachedItemNode): boolean {
    const inventory = recordOf(node.inventory);
    const encounterCreature = recordOf(node.encounter_creatures);
    return inventory?.inventory_id !== undefined
        || node.inventory_id !== undefined
        || encounterCreature?.encounter_creature_id !== undefined
        || node.encounter_creature_id !== undefined
        || recordOf(node.player_characters) !== null
        || node.status_id !== undefined
        || node.status_i !== undefined;
}

/** Harvest a referenced-names map from a single (full) item_arr blob and all its nested entries. */
export function buildNamesMapFromBlob(blob: unknown): ReferencedNames {
    const map: ReferencedNames = {};
    if (blob === null || typeof blob !== 'object') return map;

    const seen = new WeakSet<object>();
    const stack: unknown[] = [blob];

    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;

        if (Array.isArray(node)) {
            for (const child of node) {
                if (child !== null && typeof child === 'object') stack.push(child);
            }
            continue;
        }

        const nodeRecord = recordOf(node);
        if (!nodeRecord || seen.has(nodeRecord)) continue;
        seen.add(nodeRecord);

        const id = itemsCol<string>(nodeRecord, 'item_id');
        if (id && !isRuntimeItemInstance(nodeRecord)) {
            const key = String(id).toUpperCase();
            // First writer wins so a full copy's data is not clobbered by a later shell.
            if (map[key] === undefined) {
                const entry: { item_name: string; item_type: string; pic_url?: string } = {
                    item_name: String(itemsCol(nodeRecord, 'item_name') ?? ''),
                    item_type: String(itemsCol(nodeRecord, 'item_type') ?? ''),
                };
                const picUrl = picturesOf(nodeRecord, 'item')[0]?.picture_url;
                if (picUrl) entry.pic_url = String(picUrl);
                map[key] = entry;
            }
        }

        // Recurse into every nested object/array except the leaf data buckets.
        for (const k of Object.keys(nodeRecord)) {
            if (SKIP_KEYS.has(k)) continue;
            const v = nodeRecord[k];
            if (v !== null && typeof v === 'object') stack.push(v);
        }
    }

    // Catalog-as-store fold (Stage 3): the root `catalog` carries interned identities for
    // user-authored shells stripped to bare UUIDs (which the harvest above can't see — there's no
    // embedded shell left) plus full-defs. Fold them so nameOf() resolves a bare ref with zero
    // network. Identity entries are {item_name,item_type}; full-defs carry items.{...}. The harvest
    // wins first (full copies), so this only fills gaps.
    foldCatalogIntoNames(map, recordOf(blob)?.catalog);

    return map;
}

/** Gap-fill `map` from one catalog-as-store object. Identity entries are {item_name,item_type};
 * full-defs carry items.{...}. first-writer-wins: existing entries (the blob harvest / fuller
 * copies) keep precedence. */
function foldCatalogIntoNames(map: ReferencedNames, catalog: unknown): void {
    const cat = recordOf(catalog);
    if (!cat) return;
    for (const uuid of Object.keys(cat)) {
        const key = String(uuid).toUpperCase();
        if (map[key] !== undefined) continue;
        const d = recordOf(cat[uuid]);
        if (!d) continue;
        const items = recordOf(d.items);
        const item_name = String((d.item_name ?? items?.item_name) ?? '');
        const item_type = String((d.item_type ?? items?.item_type) ?? '');
        if (item_name !== '' || item_type !== '') map[key] = { item_name, item_type };
    }
}

/**
 * Gap-fill a names map from every cached ROOT blob's catalog (casterCache / objectCache /
 * encounterCache — the master def store). A nested object rendered from cache (e.g. a creature-held
 * inventory in render-from-cache) carries only its OWN — often absent — catalog, but a
 * cross-referenced def (the spell behind a charged_spell / charged_ability row, a classification)
 * is interned into the CREATURE / ENCOUNTER root catalog where the item lives, not the sub-node. So
 * nameOf() on that ref resolved to the bare UUID and rendered blank ("Charged Spell:" with no name).
 * Folding the roots mirrors the root-catalog UNION findFullNestedItem (find-cached-item.ts) and
 * _ftCatalogIdentity (array-types.ts) already do for layer/identity reads, and the encounter-root
 * fold buildCardNamesMap does for creature header chips. Gap-fill only: the blob-local harvest keeps
 * precedence. Zero network.
 */
export function foldAmbientRootCatalogsIntoNames(names: ReferencedNames): ReferencedNames {
    if (typeof window === 'undefined') return names;
    const ft = (window as NamesMapWindow).FateTide;
    for (const m of ft ? [ft.casterCache, ft.objectCache, ft.encounterCache] : []) {
        const cache = recordOf(m);
        const values = cache?.values;
        if (typeof values !== 'function') continue;
        const roots = values.call(m) as unknown;
        if (!roots || typeof (roots as { [Symbol.iterator]?: unknown })[Symbol.iterator] !== 'function') continue;
        for (const root of roots as Iterable<unknown>) {
            foldCatalogIntoNames(names, recordOf(root)?.catalog);
        }
    }
    // Page-local JSON islands (the character creator's saved-character island) are lookup roots for
    // findFullNestedItem too, and are the ONLY root on that page — a saved inventory row rendered from
    // the island named its refs from nowhere ("Quick Ability · unresolved reference", 2026-10-10).
    for (const root of pageIslandRoots()) {
        foldCatalogIntoNames(names, root.catalog);
    }
    return names;
}
